import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/index'
import { checkRoutePermission, findFirstAllowedRoute } from './guards'

// Layouts
import MainLayout from '@/layouts/MainLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'

// Auth views (lazy loaded)
const LoginView = () => import('@/views/auth/LoginView.vue')
const Register = () => import('@/views/auth/Register.vue')
const ForgotPasswordView = () => import('@/views/auth/ForgotPasswordView.vue')

// Maintenance & error views (lazy loaded)
const MaintenanceView = () => import('@/views/maintenance/MaintenanceView.vue')
const AccessDeniedView = () => import('@/views/errors/AccessDeniedView.vue')
const LicenseExpiredView = () => import('@/views/errors/LicenseExpiredView.vue')

// App views (lazy loaded)
const DashboardView = () => import('@/views/dashboard/Dashboard.vue')
const ProductsView = () => import('@/views/products/ProductsView.vue')
const ProductDetailView = () => import('@/views/products/ProductDetailView.vue')
const SalesView = () => import('@/views/sales/SalesView.vue')
const SaleSimple = () => import('@/views/sales/SaleSimple.vue')
const SaleDiffere = () => import('@/views/sales/SaleDiffere.vue')
const SaleDetailsView = () => import('@/views/sales/SaleDetailsView.vue')
const SaleInvoicesView = () => import('@/views/saleInvoices/SaleInvoicesView.vue')
const SaleInvoiceDetailsView = () => import('@/views/saleInvoices/SaleInvoiceDetailsView.vue')
const CustomerList = () => import('@/views/customers/CustomerList.vue')
const CategoriesView = () => import('@/views/categories/CategoriesView.vue')
const UsersView = () => import('@/views/users/UsersView.vue')
const UserDetailView = () => import('@/views/users/UserDetailView.vue')
const SettingsView = () => import('@/views/settings/SettingsView.vue')
const ProfileView = () => import('@/views/profile/ProfileView.vue')
const CustomerDetail = () => import('@/views/customers/CustomerDetail.vue')
const SuppliersView = () => import('@/views/suppliers/SuppliersView.vue')
const SupplierDetailView = () => import('@/views/suppliers/SupplierDetailView.vue')
const ProductRegularizationsView = () => import('@/views/products/ProductRegularizationsView.vue')
const PurchaseOrdersView = () => import('@/views/purchaseOrders/PurchaseOrdersView.vue')
const CreatePurchaseOrderView = () => import('@/views/purchaseOrders/CreatePurchaseOrderView.vue')
const PurchaseOrderDetailsView = () => import('@/views/purchaseOrders/PurchaseOrderDetailsView.vue')
const SupplierInvoicesView = () => import('@/views/supplierInvoices/SupplierInvoicesView.vue')
const SupplierInvoiceDetailsView = () => import('@/views/supplierInvoices/SupplierInvoiceDetailsView.vue')
const CreateSupplierInvoiceView = () => import('@/views/supplierInvoices/CreateSupplierInvoiceView.vue')
const ProformaInvoicesView = () => import('@/views/proformaInvoices/ProformaInvoicesView.vue')
const CreateProformaInvoiceView = () => import('@/views/proformaInvoices/CreateProformaInvoiceView.vue')
const ProformaInvoiceDetailsView = () => import('@/views/proformaInvoices/ProformaInvoiceDetailsView.vue')

// Inventory views (lazy loaded)
const InventoryOverview = () => import('@/views/inventory/InventoryOverview.vue')
const InventoryHistory = () => import('@/views/inventory/InventoryHistory.vue')
const CreateInventory = () => import('@/views/inventory/CreateInventory.vue')
const DoingInventory = () => import('@/views/inventory/DoingInventory.vue')
const InventoryDetails = () => import('@/views/inventory/InventoryDetails.vue')
const StockWithdrawalList = () => import('@/views/stockWithdrawal/StockWithdrawalList.vue')
const CreateStockWithdrawalView = () => import('@/views/stockWithdrawal/CreateStockWithdrawalView.vue')
const StockWithdrawalDetailsView = () => import('@/views/stockWithdrawal/StockWithdrawalDetailsView.vue')
// Cash Register views (lazy loaded)
const CashRegisterHomeView = () => import('@/views/cashRegister/CashRegisterHomeView.vue')
const CashRegistersHistory = () => import('@/components/cashRegister/CashRegistersHistory.vue')

const routes = [
    // Route de maintenance (doit être avant toutes les autres pour bloquer la navigation)
    {
        path: '/maintenance',
        name: 'maintenance',
        component: MaintenanceView,
        meta: {
            title: 'Maintenance'
        }
    },

    // Route licence expirée
    {
        path: '/license-expired',
        name: 'license-expired',
        component: LicenseExpiredView,
        meta: {
            title: 'Licence expirée'
        }
    },

    // Redirection de la racine (le guard redirigera vers la bonne page selon les permissions)
    {
        path: '/',
        redirect: '/dashboard'
    },

    // Routes d'authentification
    {
        path: '/',
        component: AuthLayout,
        children: [
            {
                path: 'login',
                name: 'login',
                component: LoginView,
                meta: {
                    guest: true,
                    title: 'Connexion'
                }
            },
            {
                path: 'register',
                name: 'register',
                component: Register,
                meta: {
                    guest: true,
                    title: 'Inscription'
                }
            },
            {
                path: 'forgot-password',
                name: 'forgot-password',
                component: ForgotPasswordView,
                meta: {
                    guest: true,
                    title: 'Mot de passe oublié'
                }
            }
        ]
    },

    // Route standalone pour l'inventaire mobile (sans layout)
    {
        path: '/doing-inventory',
        name: 'doing-inventory',
        component: DoingInventory,
        meta: {
            requiresAuth: true,
            title: 'Inventaire en cours',
            permission: 'physical-inventories'
        }
    },

    // Routes protégées (application principale)
    {
        path: '/',
        component: MainLayout,
        meta: { requiresAuth: true },
        children: [
            // TABLEAU DE BORD
            {
                path: 'dashboard',
                name: 'dashboard',
                component: DashboardView,
                meta: { title: 'Tableau de bord', permission: 'dashboard' }
            },

            // MODULE VENTES & CAISSES
            {
                path: 'cash-registers',
                name: 'cash-registers',
                component: CashRegisterHomeView,
                meta: { title: 'Caisses', permission: 'cash-registers' }
            },
            {
                path: 'cash-registers-history',
                name: 'cash-registers-history',
                component: CashRegistersHistory,
                meta: { title: 'Historique des caisses', permission: 'cash-registers' }
            },
            {
                path: '/sales/simple',
                name: 'sale-simple',
                component: SaleSimple,
                meta: { title: 'Vente Comptant', permission: 'make-sale' }
            },
            {
                path: '/sales/differe',
                name: 'sale-differe',
                component: SaleDiffere,
                meta: { title: 'Vente Différée', permission: 'make-sale' }
            },
            {
                path: 'sales',
                name: 'sales',
                component: SalesView,
                meta: { title: 'Historique des ventes', permission: 'sales' }
            },
            {
                path: '/sales/:id',
                name: 'sale-details',
                component: SaleDetailsView,
                meta: { title: 'Détails de la vente', permission: 'sales.view' },
                props: true
            },
            {
                path: '/sale-invoices',
                name: 'sale-invoices',
                component: SaleInvoicesView,
                meta: { title: 'Factures clients' }
            },
            {
                path: '/sale-invoices/:id',
                name: 'sale-invoice-details',
                component: SaleInvoiceDetailsView,
                meta: { title: 'Détails de la facture', permission: 'sale-invoices' },
                props: true
            },
            {
                path: 'proforma-invoices',
                name: 'proforma-invoices',
                component: ProformaInvoicesView,
                meta: { title: 'Factures Proforma', permission: 'sales' }
            },
            {
                path: 'proforma-invoices/new',
                name: 'create-proforma-invoice',
                component: CreateProformaInvoiceView,
                meta: { title: 'Nouvelle facture proforma', permission: 'sales' }
            },
            {
                path: 'proforma-invoices/:id',
                name: 'proforma-invoice-details',
                component: ProformaInvoiceDetailsView,
                meta: { title: 'Details facture proforma', permission: 'sales' },
                props: true
            },

            // MODULE STOCK & INVENTAIRE
            {
                path: 'products',
                name: 'products',
                component: ProductsView,
                meta: { title: 'Produits', permission: 'products' }
            },
            {
                path: 'stock-withdrawals',
                name: 'stock-withdrawals',
                component: StockWithdrawalList,
                meta: { title: 'Retraits de stock', permission: 'stock-withdrawals' }
            },
            {
                path: '/products/:id',
                name: 'product-detail',
                component: ProductDetailView,
                meta: { title: 'Détails du produit', permission: 'products.view' },
                props: true
            },
            {
                path: 'categories',
                name: 'categories',
                component: CategoriesView,
                meta: { title: 'Catégories', permission: 'categories' }
            },
            {
                path: 'inventory/overview',
                name: 'inventory-overview',
                component: InventoryOverview,
                meta: { title: 'État du stock', permission: 'inventory' }
            },
            {
                path: 'inventory/new',
                name: 'inventory-create',
                component: CreateInventory,
                meta: { title: 'Nouvel inventaire', permission: 'physical-inventories.create' }
            },
            {
                path: 'inventory/physical',
                name: 'inventory-history',
                component: InventoryHistory,
                meta: { title: 'Historique inventaires', permission: 'physical-inventories' }
            },
            {
                path: 'inventory/:id',
                name: 'inventory-details',
                component: InventoryDetails,
                meta: { title: 'Détails inventaire', permission: 'physical-inventories' }
            },
            {
                path: 'product-regularizations',
                name: 'product-regularizations',
                component: ProductRegularizationsView,
                meta: { title: 'Régularisations', permission: 'product-regularizations' }
            },
            {
                path: 'create-stock-withdrawal',
                name: 'create-stock-withdrawal',
                component: CreateStockWithdrawalView,
                meta: { title: 'Nouveau retrait de stock', permission: 'stock-withdrawals.create' }
            },
            {
                path: 'stock-withdrawals/:id',
                name: 'stock-withdrawal-details',
                component: StockWithdrawalDetailsView,
                meta: { title: 'Détails du retrait de stock', permission: 'stock-withdrawals' }
            },

            // MODULE ACHATS & FOURNISSEURS
            {
                path: 'suppliers',
                name: 'suppliers',
                component: SuppliersView,
                meta: { title: 'Fournisseurs', permission: 'suppliers' }
            },
            {
                path: '/suppliers/:id',
                name: 'supplier-detail',
                component: SupplierDetailView,
                meta: { title: 'Détails fournisseur', permission: 'suppliers' },
                props: true
            },
            {
                path: 'purchase-orders',
                name: 'purchase-orders',
                component: PurchaseOrdersView,
                meta: { title: 'Commandes fournisseurs', permission: 'purchase-orders' }
            },
            {
                path: 'purchase-orders/new',
                name: 'purchase-orders-new',
                component: CreatePurchaseOrderView,
                meta: { title: 'Nouvelle commande', permission: 'purchase-orders.create' }
            },
            {
                path: 'purchase-orders/:id',
                name: 'purchase-order-details',
                component: PurchaseOrderDetailsView,
                meta: { title: 'Détails commande', permission: 'purchase-orders' },
                props: true
            },
            {
                path: 'supplier-invoices',
                name: 'supplier-invoices',
                component: SupplierInvoicesView,
                meta: { title: 'Factures fournisseurs', permission: 'supplier-invoices' }
            },
            {
                path: 'suppliers/:supplierId/invoices/new',
                name: 'supplier-invoices-new',
                component: CreateSupplierInvoiceView,
                meta: { title: 'Nouvelle facture', permission: 'supplier-invoices.create' },
                props: true
            },
            {
                path: 'supplier-invoices/:id',
                name: 'supplier-invoice-details',
                component: SupplierInvoiceDetailsView,
                meta: { title: 'Détails facture', permission: 'supplier-invoices' },
                props: true
            },

            // MODULE CLIENTS
            {
                path: 'customers',
                name: 'customers',
                component: CustomerList,
                meta: { title: 'Clients', permission: 'customers' }
            },
            {
                path: '/customers/:id',
                name: 'customer-detail',
                component: CustomerDetail,
                meta: { title: 'Détails client', permission: 'customers' },
                props: true
            },

            // MODULE ADMINISTRATION
            {
                path: 'users',
                name: 'users',
                component: UsersView,
                meta: { title: 'Utilisateurs', requiresAdmin: true }
            },
            {
                path: 'users/:id',
                name: 'user-detail',
                component: UserDetailView,
                meta: { title: 'Détails utilisateur', requiresAdmin: true },
                props: true
            },
            {
                path: 'notifications',
                name: 'notifications',
                component: () => import('@/views/notifications/NotificationsView.vue'),
                meta: { title: 'Notifications', permission: 'notifications' }
            },

            // MODULE PARAMÈTRES
            {
                path: 'settings',
                name: 'settings',
                component: SettingsView,
                meta: { title: 'Paramètres', permission: 'company-settings' }
            },
            {
                path: 'profile',
                name: 'profile',
                component: ProfileView,
                meta: { title: 'Mon profil' }
            },
            {
                path: 'access-denied',
                name: 'access-denied',
                component: AccessDeniedView,
                meta: { title: 'Accès non autorisé' }
            }
        ]
    },

    // Route par défaut (404) - le guard redirigera vers la bonne page
    {
        path: '/:catchAll(.*)',
        redirect: '/dashboard'
    }
]

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        return { top: 0 }
    }
})

// Guard de navigation avec vérification des permissions
router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();
    const isAuthenticated = authStore.isAuthenticated;
    const isAdmin = authStore.isAdmin;
    const isMaintenanceMode = sessionStorage.getItem('maintenance_mode') === 'true';
    const isLicenseExpired = sessionStorage.getItem('license_expired') === 'true';

    // Update document title
    document.title = to.meta.title ? `${to.meta.title} | SellHub` : 'SellHub';

    // Si on est en mode maintenance, bloquer toute navigation sauf vers la page de maintenance
    if (isMaintenanceMode && to.name !== 'maintenance') {
        next({ name: 'maintenance' });
        return;
    }

    // Si on n'est pas en maintenance mais qu'on essaie d'accéder à la page de maintenance, autoriser
    if (to.name === 'maintenance') {
        next();
        return;
    }

    // Si la licence a expiré, bloquer toute navigation sauf licence-expired et login
    if (isLicenseExpired && to.name !== 'license-expired' && to.name !== 'login') {
        next({ name: 'license-expired' });
        return;
    }

    if (to.name === 'license-expired') {
        next();
        return;
    }

    // Si la route nécessite une authentification
    if (to.matched.some(record => record.meta.requiresAuth)) {
        if (!isAuthenticated) {
            // Rediriger vers la page de connexion
            next({
                name: 'login',
                query: { redirect: to.fullPath }
            });
            return;
        }

        // S'assurer que les permissions sont chargées (cas du refresh de page)
        if (!isAdmin) {
            const { usePermissionsStore } = await import('@/store/modules/permissions');
            const permissionsStore = usePermissionsStore();
            if (!permissionsStore.loaded) {
                await permissionsStore.fetchPermissions();
            }
        }

        // Toujours autoriser l'accès aux pages sans restriction de permission
        if (to.name === 'profile' || to.name === 'access-denied') {
            next();
            return;
        }

        // Si la route nécessite des droits d'admin et l'utilisateur n'est pas admin
        if (to.matched.some(record => record.meta.requiresAdmin) && !isAdmin) {
            next(findFirstAllowedRoute());
            return;
        }

        // Vérifier les permissions de la route
        if (!checkRoutePermission(to)) {
            // Éviter une boucle de redirection
            const allowed = findFirstAllowedRoute();
            if (allowed.name === to.name) {
                next({ name: 'access-denied' });
            } else {
                next(allowed);
            }
        } else {
            next();
        }
    }
    // Si la route est réservée aux invités (non authentifiés)
    else if (to.matched.some(record => record.meta.guest) && isAuthenticated) {
        next(findFirstAllowedRoute());
    }
    else {
        next();
    }
});
export default router